import React, { useState } from 'react';
import moment from 'moment';
import html2pdf from 'html2pdf.js';
import numberToWords from 'number-to-words';



/**
 * LDDocSection — Memorandum of Charge/Lien Over Deposits document.
 *
 * Corresponds to sblc_section_2 from the old Best Timbers project.
 * Renders a printable A4-sized LDDoc letter with:
 *  - From / To address block
 *  - Letter No / Date
 *  - Subject paragraph with computed margin totals
 *  - 12 numbered clauses
 *  - Schedule A: FD deposit accounts table
 *  - Schedule B: DSD/RD/MD/SB/CD accounts table (empty row)
 *  - Place / Date + Yours faithfully sign-off
 *
 * Props:
 *   retrievedData {object}  Data entry record from useDataEntry()
 */

const SECTION_ID = 'bank_section_2';
const PDF_FILENAME = 'LDDoc-Details.pdf';

const PDF_OPTIONS = {
  margin: 0,
  filename: PDF_FILENAME,
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    scrollY: 0,
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: document.documentElement.scrollHeight,
  },
  jsPDF: {
    unit: 'mm',
    format: [210, 350],
    orientation: 'portrait',
  },
  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
};

const LDDocSection = ({ retrievedData }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDownload = () => {
    const element = document.getElementById(SECTION_ID);
    if (!element) return;
    setIsProcessing(true);
    html2pdf()
      .set(PDF_OPTIONS)
      .from(element)
      .save()
      .then(() => setIsProcessing(false))
      .catch(() => setIsProcessing(false));
  };

  const totalm1m2 =
    Number(retrievedData?.margin10 || 0) + Number(retrievedData?.margin5 || 0);

  const words = totalm1m2 > 0 ? numberToWords.toWords(totalm1m2) : 'zero';
  const wordsCapitalized = words.charAt(0).toUpperCase() + words.slice(1);

  const invoiceamt = retrievedData?.bgAmtAndInterestAmt || 0;
  const invoicewords =
    Number(invoiceamt) > 0 ? numberToWords.toWords(Number(invoiceamt)) : 'zero';

  return (
    <>
    <div
      id={SECTION_ID}
      style={{
        width: '100%',
        maxWidth: '794px',
        minHeight: '1123px',
        background: 'white',
      }}
    >
      <div className="px-4 my-4">
        <div className="d-flex flex-column py-2">

          {/* Title */}
          <h6 className="text-center fw-bold">
            MEMORANDUM OF CHARGE/LIEN OVER DEPOSITS WHERE DEPOSIT IS COLLATERAL
          </h6>

          {/* From / To addresses */}
          <div className="d-flex justify-content-between py-2">
            <div className="d-flex flex-column">
              <p className="status-waiver-content">From,</p>
              <p className="status-waiver-content">BEST TIMBERS</p>
              <p className="status-waiver-content">NEW NO:515, OLD NO:274,</p>
              <p className="status-waiver-content">SYDENHAMS RD,</p>
              <p className="status-waiver-content">NEHRU TIMBER MARKET</p>
              <p className="status-waiver-content">Chennai - 600112</p>
            </div>
            <div className="d-flex flex-column text-end">
              <p className="status-waiver-content">To,</p>
              <p className="status-waiver-content">THE SOUTH INDIAN BANK LIMITED</p>
              <p className="status-waiver-content">Branch: Kellys</p>
            </div>
          </div>

          {/* Letter No. + Date */}
          <div className="d-flex justify-content-between py-2">
            <p className="status-waiver-content">Letter No.</p>
            <p className="status-waiver-content">Date : {moment().format('DD-MM-YYYY')}</p>
          </div>

          {/* Subject */}
          <div className="d-flex flex-column py-2">
            <p className="status-waiver-content">Sir/Madam</p>
            <p className="status-waiver-content">
              Sub: WHEREAS at my/our request Bank has granted/agreed to extend a
              loan/limit/facility by way of SBLC facility in the account of BEST
              TIMBERS against the security of schedule mentioned deposit/s of Rs
              &nbsp;{totalm1m2 || 0}/- Rupees&nbsp;{wordsCapitalized} Only in my/our
              name(s).
            </p>
          </div>

          {/* Clauses 1 – 12 */}
          <p className="status-waiver-content py-2">
            1) Now in consideration of Bank having extended/agreed to extend a
            loan/limit/facility by way of SBLC facility of USD {invoiceamt} /-
            (USD {invoicewords} only ) in the account of BEST TIMBERS. I/WE
            hereby create Lien/charge over the schedule mentioned deposit/s, as
            security for the due repayment of amount due under the above said
            loan/limit/facility. I/WE hereby hand over the Deposit receipt/s in
            respect of the Schedule as mentioned deposit/s, duly discharged by
            me/ us to be held by the bank as security for the respect of the
            above loan/limit/facility. The lien/Charge created by me/us over the
            Schedule mentioned deposit/s shall operate as a continuing security
            for the loan availed and that may be availed in future.
          </p>
          <p className="status-waiver-content py-2">
            2) I/We hereby agree that the Bank has got the full power and
            authority to demand repayment of the entire loan amount with
            interest, at its own discretion, prior to the due date fixed for
            repayment.
          </p>
          <p className="status-waiver-content py-2">
            3) I/WE hereby agree that in the event of premature closure of
            deposit/s for set off towards the amount due to the Bank, the
            interest rate applicable for the period up to the date of premature
            closure only may be paid on the deposit/s, irrespective of the
            higher rate mentioned in the deposits receipts/s.
          </p>
          <p className="status-waiver-content py-2">
            4) I/We hereby declare and agree that the Bank has got the full
            power and authority to appropriate the schedule mentioned deposit/s
            with interest accrued thereon for the repayment of the outstanding
            balance, including interest/charges etc., on any account whatsoever
            as on date of maturity of deposit or on the date of demand by Bank,
            which ever is earlier, without further notice to me/us.
          </p>
          <p className="status-waiver-content py-2">
            5) I/WE hereby further undertake that all other incidental expenses,
            costs/charges that may be recoverable from me/us from me/us from
            time to time and interest shall be compounded monthly from the due
            date of payment of deposit/s or date of demand by the Bank,
            whichever is earlier, till payment is received by the Bank in full.
          </p>
          <p className="status-waiver-content py-2">
            6) I/WE hereby authorize the Bank to credit the Schedule mentioned
            deposit/s with interest accrued thereon to the loan/limit/facility
            account, irrespective of whether the same will create a credit
            balance or not and or to appropriate the proceeds remaining after
            appropriation to the loan account, in adjustment of other
            liabilities which may be owing by me/us to the Bank on any other
            account whatsoever and at any office of the Bank, whether singly or
            jointly with others and whether as principal or as co-obligant or as
            guarantor or as surety.
          </p>
          <p className="status-waiver-content py-2">
            7) In case of any instruction being issued by me/us to renew the
            deposit/s during the pendency of the loan and in case the Bank at
            its sole and absolute discretion allow to renew the loan facility,
            I/WE hereby agree to Bank holding such renewed deposit receipt/s as
            security in the same way as the original receipt/s with charge as
            aforesaid.
          </p>
          <p className="status-waiver-content py-2">
            8) I/WE am/are personally liable for any balance that may become due
            to the Bank under the loan/limit/facility account or any account
            whatsoever from time to time.
          </p>
          <p className="status-waiver-content py-2">
            9) I/WE further declare and agree that in the event of my/our
            requiring the Bank to advance any loan and or to extend financial
            facility/ies at any time hereafter and the Bank granting such loans
            and/or financial facility/ies whether in my/our account or in the
            account of others, it is hereby understood and accepted that such
            facility/ies were extended on the basis of this letter of Lien. Bank
            may apart from my/our personal liability, hold the Schedule
            mentioned deposit receipt/s, outstanding credit balance in the
            deposit account/s including interest accrued thereon as security for
            repayment of all or any amounts that may be outstanding at any time
            and from time to time, until this letter is cancelled by the Bank,
            after I/WE comply with all the requirements as may be required by
            the Bank.
          </p>
          <p className="status-waiver-content py-2">
            10) I/WE hereby declare that the said deposit/s has/have not been
            encumbered or assigned or pledged elsewhere and is free from all
            encumbrances. I/WE hereby expressly agree to Bank computing interest
            on daily debit balance.
          </p>
          <p className="status-waiver-content py-2">
            11) Notwithstanding anything to the contrary contained hereinbefore
            or in any agreement or otherwise, it is specifically agreed that
            Bank shall always be entitiled to have General Lien over/right of
            set off/net off against the schedule mentioned deposit/s, credit
            balance against any of my our liability, whether as borrower or
            co-obligant or guarantor. The Lien hereby created shall not in any
            way affect or prejudice your Lien ie, Banker's Lien and Bank shall
            always be entitled to exercise the Lien.
          </p>
          <p className="status-waiver-content py-2">
            12) I/WE hereby undertake not to call back the deposit/s until
            my/our liability to the Bank on account of the loan/limit/facility
            allowed in the account of Acetech Heavy Fab Pvt Ltd or any other
            account with interest accrued thereon in which I/WE am/are
            personally liable is/are fully discharged.
          </p>
        </div>

        {/* ── Schedule A: FD Deposit Accounts ── */}
        <h6 className="my-4">A. SCHEDULE OF DEPOSIT ACCOUNTS</h6>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="forward_head_label">S.NO</th>
              <th className="forward_head_label">TYPE OF DEPOSIT</th>
              <th className="forward_head_label">DEPOSIT NO &amp; DATE</th>
              <th className="forward_head_label">VALUE OF DEPOSIT</th>
              <th className="forward_head_label">DATE OF MATURITY</th>
              <th className="forward_head_label">MATURITY VALUE</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="forward_body_label">1</td>
              <td className="forward_body_label">FD</td>
              <td className="forward_body_label">
                {retrievedData?.m1AccountNumber || '-'}&nbsp;&amp;&nbsp;
                {retrievedData?.bankDetailsM1Date ? moment(retrievedData.bankDetailsM1Date).format('DD-MM-YYYY') : '-'}
              </td>
              <td className="forward_body_label">{retrievedData?.margin10 || '-'}</td>
              <td className="forward_body_label">{retrievedData?.bankDetailsM1Dom ? moment(retrievedData.bankDetailsM1Dom).format('DD-MM-YYYY') : '-'}</td>
              <td className="forward_body_label">{retrievedData?.bankDetailsM1Mv || '-'}</td>
            </tr>
            <tr>
              <td className="forward_body_label">2</td>
              <td className="forward_body_label">FD</td>
              <td className="forward_body_label">
                {retrievedData?.m2AccountNumber || '-'}&nbsp;&amp;&nbsp;
                {retrievedData?.bankDetailsM2Date ? moment(retrievedData.bankDetailsM2Date).format('DD-MM-YYYY') : '-'}
              </td>
              <td className="forward_body_label">{retrievedData?.margin5 || '-'}</td>
              <td className="forward_body_label">{retrievedData?.bankDetailsM2Dom ? moment(retrievedData.bankDetailsM2Dom).format('DD-MM-YYYY') : '-'}</td>
              <td className="forward_body_label">{retrievedData?.bankDetailsM2Mv || '-'}</td>
            </tr>
          </tbody>
        </table>

        {/* ── Schedule B: DSD/RD/MD/SB/CD Accounts ── */}
        <h6 className="my-4">B. SCHEDULE OF DSD/RD/MD/SB/CD ACCOUNTS</h6>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th className="forward_head_label">S.NO</th>
              <th className="forward_head_label">TYPE OF DEPOSIT</th>
              <th className="forward_head_label">DEPOSIT NO &amp; DATE</th>
              <th className="forward_head_label">VALUE OF DEPOSIT</th>
              <th className="forward_head_label">DATE OF MATURITY</th>
              <th className="forward_head_label">MATURITY VALUE</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="forward_body_label">1</td>
              <td className="forward_body_label"></td>
              <td className="forward_body_label"></td>
              <td className="forward_body_label"></td>
              <td className="forward_body_label"></td>
              <td className="forward_body_label"></td>
            </tr>
          </tbody>
        </table>

        {/* ── Sign-off ── */}
        <div className="row my-4">
          <div className="col-12 col-lg-6">
            <p>Place : Chennai</p>
            <p>Date :</p>
          </div>
          <div className="col-12 col-lg-6 d-flex justify-content-center">
            Yours faithfully,
          </div>
        </div>


      </div>
    </div>


    {/* ── Download Button ── */}
    <div className="d-flex justify-content-center py-2">
      <button
        type="button"
        id="lddoc-download-btn"
        className="btn btn-orange text-uppercase fw-bold px-4 py-2"
        onClick={handleDownload}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Generating PDF...
          </>
        ) : (
          <>📥 Download LDDoc</>
        )}
      </button>
    </div>
  </>);
};

export default LDDocSection;
